import { useEffect, useMemo, useState } from "react"

import { useFetchPostsByCategory } from "@/hook/useFetchPostsByCategory"
import { PostData } from "@/types/types"

import type { CategoryConfig } from "../../../tamda-media/TamdaMediaNewsLayout/types/config"
import type { Article } from "../../../tamda-media/TamdaMediaNewsLayout/types/new"
import { mapPostToArticleWithFormatImage } from "../../../tamda-media/TamdaMediaNewsLayout/utils/article"

type FetchOptions = {
  categoryIds?: string[]
  pageSize?: number
  locale?: string
  domain: string
}

type UseNewsSwiperResult = {
  posts: Article[]
  postsData: Map<string, PostData>
  loading: boolean
  selectedCategoryId: string | undefined
  setSelectedCategoryId: (categoryId: string | undefined) => void
}

export const useNewsSwiper = (
  categories: CategoryConfig[] = [],
  domain: string,
  pageSize: number = 10,
  locale: string = "vi",
): UseNewsSwiperResult => {
  const safeCategories = useMemo(() => (Array.isArray(categories) ? categories : []), [categories])

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (safeCategories.length === 0) {
      setSelectedCategoryId(undefined)
      return
    }
    if (selectedCategoryId !== undefined) {
      const stillExists = safeCategories.some(
        (c) => String(c.category?.categoryId) === selectedCategoryId,
      )
      if (!stillExists) setSelectedCategoryId(undefined)
    }
  }, [safeCategories.length, selectedCategoryId])

  const categoryIds = useMemo(() => {
    if (!selectedCategoryId) return []
    return [selectedCategoryId]
  }, [selectedCategoryId])

  const fetchOptions: FetchOptions = {
    categoryIds,
    pageSize,
    locale,
    domain
  }



  const { posts: rawPosts, loading } = useFetchPostsByCategory(fetchOptions)

  const { posts, postsData } = useMemo(() => {
    if (!rawPosts || rawPosts.length === 0) {
      return { posts: [] as Article[], postsData: new Map<string, PostData>() }
    }

    const articles = rawPosts.map(mapPostToArticleWithFormatImage)
    const dataMap = new Map<string, PostData>()
    rawPosts.forEach((post: PostData) => {
      dataMap.set(post.id.toString(), post)
    })

    return { posts: articles, postsData: dataMap }
  }, [rawPosts])

  return {
    posts,
    postsData,
    loading,
    selectedCategoryId,
    setSelectedCategoryId,
  }
}
